/**
 * LegOffers — one compact banner per leg for the airline offers that apply to
 * this trip and traveller: the title, why it is yours, the deadline, the code to
 * copy (promo codes) and the source. Only the first offer (the caller orders
 * them: most urgent, else most valuable) is expanded; the rest sit behind
 * "N more". The close button hands control back to LegWatch.
 */
import { useEffect, useRef, useState } from "react";
import { Check, Copy, Tag, X } from "lucide-react";
import type { PlanOffer } from "@/lib/fareOptionsTypes";
import { offerView, safeHref } from "./fareLadderView";

interface LegOffersProps {
  /** Already ordered: the first is the one that leads. */
  offers: readonly PlanOffer[] | undefined;
  /** How many offers show before the rest sit behind "N more". */
  visible?: number;
  onClose?: () => void;
}

function CodeChip({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard blocked: the code is still on screen to read and type.
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      aria-label={"Copy code " + code}
      className="inline-flex min-h-[28px] items-center gap-1 rounded border border-dashed border-[hsl(var(--primary))]/60 bg-card px-2 py-0.5 font-mono text-xs font-semibold tracking-wide text-foreground transition-colors hover:bg-muted motion-reduce:transition-none"
    >
      {code}
      {copied ? (
        <Check className="h-3 w-3 text-emerald-700 dark:text-emerald-400" aria-hidden="true" />
      ) : (
        <Copy className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
      )}
      <span className="sr-only" role="status">{copied ? "Copied" : ""}</span>
    </button>
  );
}

export default function LegOffers({ offers, visible = 1, onClose }: LegOffersProps) {
  const [all, setAll] = useState(false);
  if (!offers || offers.length === 0) return null;
  const views = offers.map(offerView);
  const shown = all ? views : views.slice(0, visible);
  const hidden = views.length - shown.length;
  return (
    <section
      aria-label="Offers for this trip"
      className="relative rounded-lg border border-[hsl(var(--primary))]/25 bg-accent/60 px-3 py-2 pr-9 text-xs"
    >
      <button
        type="button"
        onClick={() => onClose?.()}
        aria-label="Hide offers"
        className="absolute right-1.5 top-1.5 inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground motion-reduce:transition-none"
      >
        <X className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
      <ul className="space-y-2">
        {shown.map((o) => {
          const href = safeHref(o.sourceUrl);
          return (
            <li key={o.id} className="flex items-start gap-2">
              <Tag className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(var(--accent-foreground))]" aria-hidden="true" />
              <div className="min-w-0 space-y-0.5">
                <p className="font-medium text-foreground">{o.title}</p>
                <p className="text-muted-foreground">{o.whyYou}</p>
                <p className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-muted-foreground">
                  {o.code && (
                    <>
                      <CodeChip code={o.code} />
                      <span>Use at checkout</span>
                    </>
                  )}
                  {o.bookBy && <span className="font-medium text-foreground">{o.bookBy}</span>}
                  {href ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline underline-offset-2 hover:text-foreground"
                    >
                      {o.sourceLabel}
                    </a>
                  ) : (
                    <span>{o.sourceLabel}</span>
                  )}
                </p>
              </div>
            </li>
          );
        })}
      </ul>
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setAll(true)}
          className="mt-1.5 text-[11px] font-medium text-[hsl(var(--primary))] hover:underline"
        >
          {hidden} more {hidden === 1 ? "offer" : "offers"}
        </button>
      )}
    </section>
  );
}
